import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemButton,
  Divider,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Tab,
  Tabs
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useSensorData from '../hooks/useSensorData';
import { fetchSensorPlacement, getSensorsForTunnel } from '../api/sensorApi';

const SensorList = () => {
  const navigate = useNavigate();
  const { loading, data, error, selectedTunnel, changeTunnel } = useSensorData();
  const [sensors, setSensors] = useState([]);
  const [filteredSensors, setFilteredSensors] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'placement'
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  
  // Load sensor placement data
  useEffect(() => {
    const loadPlacementData = async () => {
      try {
        const placement = await fetchSensorPlacement();
        setPlacements(placement);
      } catch (err) {
        console.error('Error loading placement data:', err);
      }
    };
    
    loadPlacementData();
  }, []);
  
  // Extract and process sensor data
  useEffect(() => {
    if (data?.originalData) {
      // Extract unique sensors with their tunnels
      const sensorMap = new Map();
      
      data.originalData.forEach(item => {
        if (!sensorMap.has(item.sensor)) {
          sensorMap.set(item.sensor, {
            id: item.sensor,
            tunnel: item.tunnel,
            lastReading: {
              m1: item.m1,
              m2: item.m2,
              timestamp: item.timestamp
            },
            placement: null,  // Will be filled later
            position: null    // Will be filled later
          });
        } else {
          // Update with most recent reading
          const existing = sensorMap.get(item.sensor);
          const existingTime = new Date(existing.lastReading.timestamp);
          const newTime = new Date(item.timestamp);
          
          if (newTime > existingTime) {
            existing.lastReading = {
              m1: item.m1,
              m2: item.m2,
              timestamp: item.timestamp
            };
          }
        }
      });
      
      // Add placement and position information
      if (placements.length > 0) {
        for (const placement of placements) {
          for (const [position, sensorIds] of Object.entries(placement.sensors)) {
            for (const sensorId of sensorIds) {
              if (sensorMap.has(sensorId)) {
                const sensor = sensorMap.get(sensorId);
                sensor.placement = placement.placement;
                sensor.position = position;
              }
            }
          }
        }
      }
      
      const allSensors = Array.from(sensorMap.values());
      setSensors(allSensors);
      
      // Apply tunnel filter if selected
      if (selectedTunnel) {
        setFilteredSensors(allSensors.filter(sensor => sensor.tunnel === selectedTunnel));
      } else {
        setFilteredSensors(allSensors);
      }
    }
  }, [data, selectedTunnel, placements]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">Error loading sensor data</Typography>
        <Typography>{error.message}</Typography>
      </Box>
    );
  }
  
  // Get unique placements for the current tunnel selection
  const uniquePlacements = Array.from(
    new Set(filteredSensors.map(sensor => sensor.placement).filter(Boolean))
  ).sort();
  
  // Function to render sensor information in a card
  const renderSensorCard = (sensor) => {
    // Position-based colors
    const positionColors = {
      low: '#4caf50',     // green
      middle: '#2196f3',  // blue
      roof: '#f44336'     // red
    };
    
    const avatarBgColor = sensor.position ? positionColors[sensor.position] : '#9e9e9e';
    
    return (
      <Grid item xs={12} sm={6} md={4} key={sensor.id}>
        <Card sx={{ height: '100%' }}>
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: avatarBgColor }}>
                {sensor.id.slice(-2)}
              </Avatar>
            }
            title={sensor.id}
            subheader={`${sensor.placement || 'Unknown'} - ${sensor.position || 'Unknown'}`}
          />
          <CardContent>
            <Typography variant="body2" gutterBottom>
              <strong>Readings:</strong> m1: {sensor.lastReading.m1 !== null ? sensor.lastReading.m1.toFixed(1) : 'N/A'}, 
              m2: {sensor.lastReading.m2 !== null ? sensor.lastReading.m2.toFixed(1) : 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last updated: {new Date(sensor.lastReading.timestamp).toLocaleString()}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={`Tunnel: ${sensor.tunnel}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          All Sensors
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {data?.tunnels && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="sensor-tunnel-select-label">Filter by Tunnel</InputLabel>
              <Select
                labelId="sensor-tunnel-select-label"
                id="sensor-tunnel-select"
                value={selectedTunnel || ''}
                label="Filter by Tunnel"
                onChange={(e) => changeTunnel(e.target.value || null)}
              >
                <MenuItem value="">All Tunnels</MenuItem>
                {data.tunnels.map(tunnel => (
                  <MenuItem key={tunnel} value={tunnel}>{tunnel}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
      
      {/* View Mode Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          aria-label="sensor view mode"
        >
          <Tab label="List View" value="list" />
          <Tab label="Placement View" value="placement" />
        </Tabs>
      </Box>
      
      {viewMode === 'list' && (
        <Paper sx={{ mt: 3 }}>
          <List>
            {filteredSensors.map((sensor, index) => (
              <React.Fragment key={sensor.id}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {sensor.placement && (
                        <Chip 
                          label={`${sensor.placement} - ${sensor.position}`}
                          color="secondary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      <Chip 
                        label={`Tunnel: ${sensor.tunnel}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => navigate('/sensor/m1')}>
                    <ListItemText 
                      primary={sensor.id} 
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" component="span">
                            m1: {sensor.lastReading.m1 !== null ? sensor.lastReading.m1.toFixed(1) : 'N/A'}, 
                            m2: {sensor.lastReading.m2 !== null ? sensor.lastReading.m2.toFixed(1) : 'N/A'}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Last updated: {new Date(sensor.lastReading.timestamp).toLocaleString()}
                          </Typography>
                        </React.Fragment>
                      } 
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      
      {viewMode === 'placement' && (
        <>
          {/* Placement Tabs */}
          {uniquePlacements.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={selectedPlacement || uniquePlacements[0]}
                onChange={(e, newValue) => setSelectedPlacement(newValue)}
                aria-label="placement selector"
                variant="scrollable"
                scrollButtons="auto"
              >
                {uniquePlacements.map(placement => (
                  <Tab key={placement} label={placement} value={placement} />
                ))}
              </Tabs>
            </Box>
          )}
          
          {/* Grid of Sensor Cards */}
          <Grid container spacing={3}>
            {filteredSensors
              .filter(sensor => !selectedPlacement || sensor.placement === selectedPlacement)
              .map(renderSensorCard)}
          </Grid>
          
          {/* Legend */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sensor Position Legend:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#4caf50' }}>L</Avatar>}
                label="Low" 
                variant="outlined"
              />
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#2196f3' }}>M</Avatar>}
                label="Middle" 
                variant="outlined"
              />
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#f44336' }}>R</Avatar>}
                label="Roof"
                variant="outlined"
              />
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default SensorList;